import {
  Flex,
  StarHalfIcon,
  StarFilledIcon,
  Typography,
} from '@iheartjane/reefer';
import type { PropsWithChildren } from 'react';

export type StarRatingProps = {
  aggregateRating: number;
  /**
   * Whether to show the full 5 star rating or single star rating
   * @default false
   */
  isCondensed?: boolean;
  /**
   * If this rating is on a pdp then we need to add a margin bottom
   */
  isPdp?: boolean;
  /**
   * Text shown after the review count
   * @default ` reviews`
   */
  ratingSuffix?: string;
  reviewCount?: number;
};

type StarRatingContainerProps = {
  ariaLabel: string;
  isCondensed?: boolean;
  isPdp?: boolean;
  ratingText: string;
};

const StarRatingContainer = ({
  children,
  ariaLabel,
  isPdp,
  ratingText,
}: PropsWithChildren<StarRatingContainerProps>) => {
  return (
    <Flex
      alignItems="center"
      ariaLabel={ariaLabel}
      flexDirection="row"
      mb={isPdp ? 8 : undefined}
    >
      <Flex pr={4}>{children}</Flex>
      <Typography variant="small-body-regular" maxLines={1}>
        {ratingText}
      </Typography>
    </Flex>
  );
};

const CondensedStarRating = (props: StarRatingContainerProps) => (
  <StarRatingContainer isCondensed {...props}>
    <StarFilledIcon size="sm" />
  </StarRatingContainer>
);

export const StarRating = ({
  aggregateRating,
  ratingSuffix = ' ratings',
  reviewCount,
  isCondensed = false,
  isPdp = false,
}: StarRatingProps) => {
  const sharedRatingText = `${aggregateRating.toFixed(1)}`;
  const condensedRatingText = reviewCount
    ? `${sharedRatingText} (${reviewCount})`
    : sharedRatingText;
  const fiveStarRatingText = `${sharedRatingText} (${reviewCount}${ratingSuffix})`;

  return isCondensed ? (
    <CondensedStarRating
      ariaLabel="condensed star rating"
      ratingText={condensedRatingText}
    />
  ) : (
    <StarRatingContainer
      ariaLabel={`star rating-${aggregateRating}`}
      ratingText={fiveStarRatingText}
      isPdp={isPdp}
    >
      {[1, 2, 3, 4, 5].map((number) => {
        if (aggregateRating < number && aggregateRating > number - 0.6) {
          return <StarHalfIcon size="sm" key={number} />;
        }

        const starColor = aggregateRating >= number ? 'gold' : 'grays-light';

        return <StarFilledIcon size="sm" key={number} color={starColor} />;
      })}
    </StarRatingContainer>
  );
};
